import {
  Box, Grid, Paper, Typography, Stack, Avatar, Chip, LinearProgress,
} from '@mui/material';
import { keyframes } from '@mui/system';
import SpeedIcon from '@mui/icons-material/Speed';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import StarIcon from '@mui/icons-material/Star';

const fadeUp = keyframes`
  from { opacity:0; transform:translateY(20px); }
  to   { opacity:1; transform:translateY(0); }
`;

const RadarStat = ({ label, value, color }) => (
  <Box mb={2}>
    <Stack direction="row" justifyContent="space-between" mb={.6}>
      <Typography variant="caption" sx={{ color: 'rgba(0,0,0,.45)', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</Typography>
      <Typography variant="caption" sx={{ color, fontWeight: 800 }}>{value}/100</Typography>
    </Stack>
    <LinearProgress variant="determinate" value={value} sx={{
      height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,.07)',
      '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 },
    }} />
  </Box>
);

const MiniCard = ({ label, value, icon, color, delay }) => (
  <Paper elevation={0} sx={{
    p: 2.5, borderRadius: 3,
    bgcolor: '#fff',
    border: `1px solid ${color}30`,
    textAlign: 'center',
    animation: `${fadeUp} .6s ease forwards`, animationDelay: delay, opacity: 0,
    '&:hover': { border: `1px solid ${color}80`, transform: 'translateY(-3px)', boxShadow: `0 8px 24px ${color}18` },
    transition: 'all .3s',
  }}>
    <Box sx={{ color, mb: 1 }}>{icon}</Box>
    <Typography variant="h4" sx={{ color: '#1A1A2E', fontWeight: 900 }}>{value}</Typography>
    <Typography variant="caption" sx={{ color: 'rgba(0,0,0,.4)' }}>{label}</Typography>
  </Paper>
);

const PlayerDashboard = ({ user }) => {
  const s = user.playerStats || {};
  return (
    <Box sx={{ bgcolor: '#F5F6FA', minHeight: '100vh', p: 3 }}>
      {/* Profile hero */}
      <Paper elevation={0} sx={{
        p: 4, mb: 4, borderRadius: 3, overflow: 'hidden', position: 'relative',
        background: 'linear-gradient(135deg, rgba(212,175,55,.10) 0%, rgba(34,197,94,.06) 100%)',
        border: '1px solid rgba(212,175,55,.25)',
        animation: `${fadeUp} .5s ease forwards`, opacity: 0,
        boxShadow: '0 4px 24px rgba(212,175,55,.08)',
      }}>
        <Box sx={{ position: 'absolute', top: -30, right: -30, width: 150, height: 150, borderRadius: '50%', bgcolor: 'rgba(212,175,55,.08)' }} />
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={3}>
          <Avatar sx={{
            width: 72, height: 72,
            bgcolor: 'rgba(212,175,55,.15)',
            color: '#B8930A',
            fontWeight: 900, fontSize: '1.8rem',
            border: '2px solid rgba(212,175,55,.4)',
            boxShadow: '0 4px 12px rgba(212,175,55,.2)',
          }}>
            {user.firstName?.[0]}{user.lastName?.[0]}
          </Avatar>
          <Box flex={1}>
            <Typography variant="h4" sx={{ color: '#1A1A2E', fontWeight: 900 }}>{user.firstName} {user.lastName}</Typography>
            <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" gap={1}>
              <Chip label="Joueur" size="small" sx={{ bgcolor: 'rgba(34,197,94,.12)', color: '#16A34A', fontWeight: 700 }} />
              <Chip label={user.entity ? 'Club assigné' : 'Sans club'} size="small" sx={{ bgcolor: 'rgba(0,0,0,.06)', color: 'rgba(0,0,0,.5)' }} />
              {user.isActive && <Chip label="Actif" size="small" sx={{ bgcolor: 'rgba(34,197,94,.1)', color: '#16A34A' }} />}
            </Stack>
          </Box>
          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography variant="caption" sx={{ color: 'rgba(0,0,0,.35)' }}>Note globale</Typography>
            <Typography variant="h2" sx={{ color: '#B8930A', fontWeight: 900, lineHeight: 1 }}>
              {s.technicalRating || '—'}
            </Typography>
            <StarIcon sx={{ color: '#D4AF37', fontSize: 16 }} />
          </Box>
        </Stack>
      </Paper>

      {/* Quick stats */}
      <Grid container spacing={2} mb={4}>
        {[
          { label: 'Matchs',   value: s.matchesPlayed  || 0, icon: <SpeedIcon />,         color: '#D4AF37', delay: '.1s' },
          { label: 'Buts',     value: s.goalsScored    || 0, icon: <StarIcon />,           color: '#22C55E', delay: '.2s' },
          { label: 'Passes D', value: s.assists        || 0, icon: <SportsKabaddiIcon />, color: '#3B82F6', delay: '.3s' },
          { label: 'Minutes',  value: s.minutesPlayed  || 0, icon: <FitnessCenterIcon />, color: '#F97316', delay: '.4s' },
        ].map((c, i) => (
          <Grid item xs={6} sm={3} key={i}><MiniCard {...c} /></Grid>
        ))}
      </Grid>

      {/* Attributes */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{
            p: 3, borderRadius: 3,
            bgcolor: '#fff',
            border: '1px solid rgba(212,175,55,.18)',
            boxShadow: '0 2px 16px rgba(0,0,0,.06)',
            animation: `${fadeUp} .6s ease forwards`, animationDelay: '.5s', opacity: 0,
          }}>
            <Typography variant="h6" sx={{ color: '#1A1A2E', fontWeight: 700, mb: 3 }}>Attributs Physiques</Typography>
            <RadarStat label="Vitesse"    value={s.speed   || 0} color="#D4AF37" />
            <RadarStat label="Endurance"  value={s.stamina || 0} color="#22C55E" />
            <RadarStat label="Force"      value={s.strength|| 0} color="#3B82F6" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{
            p: 3, borderRadius: 3,
            bgcolor: '#fff',
            border: '1px solid rgba(212,175,55,.18)',
            boxShadow: '0 2px 16px rgba(0,0,0,.06)',
            animation: `${fadeUp} .6s ease forwards`, animationDelay: '.6s', opacity: 0,
          }}>
            <Typography variant="h6" sx={{ color: '#1A1A2E', fontWeight: 700, mb: 3 }}>Statistiques de Jeu</Typography>
            <RadarStat label="Technique"              value={s.technicalRating || 0} color="#F97316" />
            <RadarStat label="Précision des passes"   value={s.passAccuracy    || 0} color="#A855F7" />
            <Stack direction="row" spacing={2} mt={3}>
              {[
                { label: 'Cartons Jaunes', value: s.yellowCards || 0, color: '#D97706' },
                { label: 'Cartons Rouges', value: s.redCards    || 0, color: '#DC2626' },
              ].map((c, i) => (
                <Paper key={i} elevation={0} sx={{
                  flex: 1, p: 2, borderRadius: 2,
                  bgcolor: `${c.color}0D`,
                  border: `1px solid ${c.color}30`,
                  textAlign: 'center',
                }}>
                  <Typography variant="h5" sx={{ color: c.color, fontWeight: 900 }}>{c.value}</Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(0,0,0,.45)' }}>{c.label}</Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default PlayerDashboard;